import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Search, ChevronDown, X, Check, Loader2, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface SelectOption {
    id: string;
    name: string;
    subtitle?: string;
}

interface AsyncSearchableSelectProps {
    /** Function to search for options - called with query string */
    searchFn: (query: string) => Promise<SelectOption[]>;
    /** Already selected option IDs - these will be shown as disabled */
    selectedIds?: string[];
    /** Called when an option is selected */
    onSelect: (option: SelectOption) => void;
    /** Pre-loaded options to show before user searches (e.g., already assigned items) */
    initialOptions?: SelectOption[];
    placeholder?: string;
    searchPlaceholder?: string;
    emptyMessage?: string;
    minSearchLength?: number;
    debounceMs?: number;
    className?: string;
    disabled?: boolean;
    showSelectedInList?: boolean;
    /** Called when user clicks "Create" in the empty state. Receives the current search query. */
    onCreateNew?: (query: string) => void;
    createNewLabel?: string;
    /** Optional ref to the trigger button, so a parent can move focus to it programmatically. */
    triggerRef?: React.Ref<HTMLButtonElement>;
    /** Optional keydown handler on the trigger button (fires only while the dropdown is closed). */
    onTriggerKeyDown?: React.KeyboardEventHandler<HTMLButtonElement>;
    /** Bump this number to open the dropdown (and focus its search box) from a parent. */
    openSignal?: number;
}

export function AsyncSearchableSelect({
    searchFn,
    selectedIds = [],
    onSelect,
    initialOptions,
    placeholder = 'Search to select...',
    searchPlaceholder = 'Type to search...',
    emptyMessage = 'No results found',
    minSearchLength = 2,
    debounceMs = 300,
    className,
    disabled = false,
    showSelectedInList = false,
    onCreateNew,
    createNewLabel = 'Create new',
    triggerRef,
    onTriggerKeyDown,
    openSignal,
}: AsyncSearchableSelectProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const initialRef = useRef<SelectOption[]>(initialOptions ?? []);
    const [options, setOptions] = useState<SelectOption[]>(initialRef.current);
    const [isLoading, setIsLoading] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const debounceRef = useRef<NodeJS.Timeout | null>(null);
    // Outside a modal dialog the dropdown is portaled to <body> with fixed
    // positioning so it is never clipped by an ancestor's overflow (e.g. the
    // table's overflow-auto wrapper). Inside a dialog we keep the original
    // in-flow absolute dropdown, since a body portal would fight the dialog's
    // focus trap (focus would be yanked out of the search input).
    const [usePortal, setUsePortal] = useState(false);
    const [menuPos, setMenuPos] = useState<{ top: number; left: number; width: number; openUp: boolean } | null>(null);
    // Store the selected option to display even if not in initialOptions
    const [selectedOptionState, setSelectedOptionState] = useState<SelectOption | null>(
        selectedIds.length > 0 && initialOptions
            ? initialOptions.find(opt => selectedIds.includes(opt.id)) || null
            : null
    );

    // Keep initial options ref in sync when prop changes (avoids identity churn)
    useEffect(() => {
        if (initialOptions) {
            initialRef.current = initialOptions;
        }
    }, [initialOptions]);

    // Update selected option state when selectedIds changes
    useEffect(() => {
        if (selectedIds.length === 0) {
            setSelectedOptionState(null);
        } else {
            // Try to find the selected option in initialOptions first
            const found = initialRef.current.find(opt => selectedIds.includes(opt.id));
            if (found) {
                setSelectedOptionState(found);
            }
            // If not found in initialOptions, keep the current selectedOptionState
            // (it will be updated when handleSelect is called)
        }
    }, [selectedIds]);

    // Close on click outside (one-time listener). The dropdown lives in a
    // portal, so check both the trigger container and the menu.
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            const inTrigger = containerRef.current?.contains(target);
            const inMenu = menuRef.current?.contains(target);
            if (!inTrigger && !inMenu) {
                setIsOpen(false);
                setSearchQuery('');
                setOptions(initialRef.current);
                setHasSearched(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Decide portal vs in-flow when opening, and (when portaling) compute the
    // dropdown position from the trigger, flipping above when there isn't enough
    // room below. Recompute while open on scroll/resize.
    useEffect(() => {
        if (!isOpen) {
            setMenuPos(null);
            return;
        }
        const inDialog = containerRef.current?.closest('[role="dialog"]') != null;
        setUsePortal(!inDialog);
        if (inDialog) return;
        const update = () => {
            if (!containerRef.current) return;
            const rect = containerRef.current.getBoundingClientRect();
            const estimatedHeight = 320;
            const spaceBelow = window.innerHeight - rect.bottom;
            const openUp = spaceBelow < estimatedHeight && rect.top > spaceBelow;
            setMenuPos({
                top: openUp ? rect.top - 4 : rect.bottom + 4,
                left: rect.left,
                width: rect.width,
                openUp,
            });
        };
        update();
        window.addEventListener('scroll', update, true);
        window.addEventListener('resize', update);
        return () => {
            window.removeEventListener('scroll', update, true);
            window.removeEventListener('resize', update);
        };
    }, [isOpen]);

    // Focus the search input when opening so you can type immediately. For the
    // portaled menu, wait until it's positioned/mounted (menuPos is set).
    useEffect(() => {
        if (!isOpen) return;
        if (usePortal && !menuPos) return;
        inputRef.current?.focus();
    }, [isOpen, usePortal, menuPos]);

    // Open the dropdown when the parent bumps openSignal (skips the initial
    // mount value), so focus can land in the search box ready to type.
    const openSignalRef = useRef(openSignal);
    useEffect(() => {
        if (openSignal === undefined || openSignal === openSignalRef.current) return;
        openSignalRef.current = openSignal;
        setIsOpen(true);
    }, [openSignal]);

    // Debounced search
    const performSearch = useCallback(async (query: string) => {
        if (query.length < minSearchLength) {
            setOptions(initialRef.current);
            setHasSearched(false);
            return;
        }

        setIsLoading(true);
        try {
            const results = await searchFn(query);
            setOptions(results);
            setHasSearched(true);
        } catch (error) {
            console.error('Search failed:', error);
            setOptions([]);
        } finally {
            setIsLoading(false);
        }
    }, [searchFn, minSearchLength]);

    // Handle search input changes with debounce
    useEffect(() => {
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }

        if (!searchQuery.trim()) {
            setOptions(initialRef.current);
            setHasSearched(false);
            return;
        }

        debounceRef.current = setTimeout(() => {
            performSearch(searchQuery);
        }, debounceMs);

        return () => {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }
        };
    }, [searchQuery, performSearch, debounceMs]);

    // Filter out selected options if needed
    const displayedOptions = showSelectedInList
        ? options
        : options.filter(opt => !selectedIds.includes(opt.id));

    const handleSelect = (option: SelectOption) => {
        onSelect(option);
        setSelectedOptionState(option);
        setSearchQuery('');
        setOptions(initialRef.current);
        setHasSearched(false);
        setIsOpen(false);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Escape') {
            setIsOpen(false);
            setSearchQuery('');
            setOptions(initialRef.current);
            setHasSearched(false);
            // Return focus to the trigger so keyboard flow continues from here.
            containerRef.current?.querySelector('button')?.focus();
        }
        if (e.key === 'Enter' && displayedOptions.length === 1) {
            e.preventDefault();
            handleSelect(displayedOptions[0]);
        }
    };

    // With an empty box, show the pre-loaded options when there are any (a
    // picker can seed useful defaults) and fall back to the hint when there are
    // none. A query that is typed but still too short always shows the hint.
    const trimmedQuery = searchQuery.trim();
    const queryTooShort = trimmedQuery.length > 0 && trimmedQuery.length < minSearchLength;
    const showTypeToSearch = queryTooShort || (!trimmedQuery && displayedOptions.length === 0);

    // Find the selected option to display in the trigger button
    // Prefer the stored state, then check current options/initial options
    const selectedOption = selectedOptionState || (selectedIds.length > 0
        ? [...initialRef.current, ...options].find(opt => selectedIds.includes(opt.id))
        : null);

    const dropdownInner = (
        <>
            {/* Search input */}
            <div className="border-b p-2">
                <div className="relative">
                    <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <input
                        ref={inputRef}
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder={searchPlaceholder}
                        className="w-full rounded-md border-0 bg-transparent py-1.5 pl-8 pr-8 text-sm focus:outline-none"
                    />
                    {searchQuery ? (
                        <button
                            type="button"
                            onClick={() => {
                                setSearchQuery('');
                                setOptions(initialRef.current);
                                setHasSearched(false);
                            }}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        >
                            <X className="h-4 w-4" />
                        </button>
                    ) : isLoading ? (
                        <Loader2 className="absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
                    ) : null}
                </div>
            </div>

            {/* Options list */}
            <div className="max-h-60 overflow-y-auto p-1">
                {isLoading ? (
                    <div className="flex items-center justify-center gap-2 px-3 py-4 text-sm text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Searching...
                    </div>
                ) : showTypeToSearch ? (
                    <div className="px-3 py-4 text-center text-sm text-muted-foreground">
                        Type at least {minSearchLength} characters to search
                    </div>
                ) : displayedOptions.length === 0 ? (
                    <div className="px-3 py-4 text-center text-sm text-muted-foreground">
                        {hasSearched ? (
                            <div className="space-y-2">
                                <div>{emptyMessage}</div>
                                {onCreateNew && searchQuery.trim() && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            onCreateNew(searchQuery.trim());
                                            setIsOpen(false);
                                            setSearchQuery('');
                                            setHasSearched(false);
                                        }}
                                        className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-sm font-medium text-primary hover:bg-accent"
                                    >
                                        <Plus className="h-3.5 w-3.5" />
                                        {createNewLabel} &ldquo;{searchQuery.trim()}&rdquo;
                                    </button>
                                )}
                            </div>
                        ) : `Type at least ${minSearchLength} characters to search`}
                    </div>
                ) : (
                    displayedOptions.map((option) => {
                        const isSelected = selectedIds.includes(option.id);
                        return (
                            <button
                                key={option.id}
                                type="button"
                                onClick={() => !isSelected && handleSelect(option)}
                                disabled={isSelected}
                                className={cn(
                                    'flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm',
                                    'hover:bg-accent focus:bg-accent focus:outline-none',
                                    isSelected && 'cursor-not-allowed opacity-50'
                                )}
                            >
                                <div>
                                    <div className="font-medium">{option.name}</div>
                                    {option.subtitle && (
                                        <div className="text-xs text-muted-foreground">
                                            {option.subtitle}
                                        </div>
                                    )}
                                </div>
                                {isSelected && <Check className="h-4 w-4 text-primary" />}
                            </button>
                        );
                    })
                )}
            </div>
        </>
    );

    return (
        <div ref={containerRef} className={cn('relative', className)}>
            {/* Trigger button */}
            <button
                ref={triggerRef}
                type="button"
                onKeyDown={onTriggerKeyDown}
                onClick={() => !disabled && setIsOpen(!isOpen)}
                disabled={disabled}
                className={cn(
                    'flex w-full items-center justify-between rounded-md border border-input bg-background px-3 py-1.5 text-sm',
                    'hover:bg-accent/50 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
                    disabled && 'cursor-not-allowed opacity-50',
                    isOpen && 'ring-2 ring-ring ring-offset-2'
                )}
            >
                {selectedOption ? (
                    <span className="truncate">{selectedOption.name}</span>
                ) : (
                    <span className="text-muted-foreground">{placeholder}</span>
                )}
                <ChevronDown className={cn('h-4 w-4 transition-transform', isOpen && 'rotate-180')} />
            </button>

            {/* Dropdown. Portaled to <body> with fixed positioning outside a
                dialog (escapes overflow clipping); kept in-flow inside one. */}
            {isOpen && (
                usePortal
                    ? menuPos && createPortal(
                        <div
                            ref={menuRef}
                            style={{
                                position: 'fixed',
                                top: menuPos.top,
                                left: menuPos.left,
                                width: menuPos.width,
                                transform: menuPos.openUp ? 'translateY(-100%)' : undefined,
                            }}
                            className="z-50 rounded-md border bg-popover shadow-lg">
                            {dropdownInner}
                        </div>,
                        document.body
                    )
                    : (
                        <div
                            ref={menuRef}
                            className="absolute z-50 mt-1 w-full rounded-md border bg-popover shadow-lg">
                            {dropdownInner}
                        </div>
                    )
            )}
        </div>
    );
}
